/**
 * 檔案用途區塊
 * @module ssoUrl.helper
 * @purpose 組合不同第三方登入頁面的授權網址與查詢參數，供前端導向 SSO 流程使用。
 * @external https://innotech.atlassian.net/browse/IN-132134
 * @external https://innotech.atlassian.net/browse/IN-94351
 * @external https://innotech.atlassian.net/browse/IN-89368
 * @external https://innotech.atlassian.net/browse/IN-54243
 * @external https://innotech.atlassian.net/browse/IN-54205
 */
import { ssoUrls } from '@shared/constants/sso-urls.constants';
import { ThirdpartyLoginMethod } from '@shared/enums';
import { Page } from '@shared/enums/route.enum';

/**
 * 宣告內容用途說明與單號關聯
 * @description 依據指定頁面類型產生對應第三方登入授權網址，並帶入 clientId、導回位置與 provider 狀態。
 * @purpose 集中管理 Apple、Google、Facebook、Zalo、Telegram 的 SSO URL 參數組裝規則。
 * @external https://innotech.atlassian.net/browse/IN-54205
 */
export const getSsoUrl = (page: Page, payload: { clientId: string; redirectUrl: string }) => {
  switch (page) {
    case Page.Apple: {
      /**
       * 宣告內容用途說明與單號關聯
       * @description 建立 Apple OAuth 所需查詢參數並序列化為 query string。
       * @purpose 確保 Apple 登入流程帶入 code 模式與 provider state。
       * @external https://innotech.atlassian.net/browse/IN-54205
       */
      const searchParams = new URLSearchParams({
        client_id: payload.clientId,
        redirect_uri: location.origin,
        response_type: 'code',
        response_mode: 'query',
        state: `${payload.redirectUrl?.toString()}?provider=${ThirdpartyLoginMethod.Apple}`,
      }).toString();
      return `${ssoUrls[Page.Apple]}?${searchParams}`;
    }
    case Page.Google: {
      /**
       * 宣告內容用途說明與單號關聯
       * @description 建立 Google OAuth 所需查詢參數並序列化為 query string。
       * @purpose 統一 Google 授權範圍、帳號選擇提示與 provider state 傳遞。
       * @external https://innotech.atlassian.net/browse/IN-54205
       */
      const searchParams = new URLSearchParams({
        client_id: payload.clientId,
        redirect_uri: location.origin,
        response_type: 'code',
        scope: 'openid email profile',
        prompt: 'select_account',
        state: `${payload.redirectUrl?.toString()}?provider=${ThirdpartyLoginMethod.Google}`,
      }).toString();
      return `${ssoUrls[Page.Google]}?${searchParams}`;
    }
    case Page.FaceBook: {
      /**
       * 宣告內容用途說明與單號關聯
       * @description 建立 Facebook OAuth 所需查詢參數並序列化為 query string。
       * @purpose 讓 Facebook 登入可回傳授權碼並保留 provider state。
       * @external https://innotech.atlassian.net/browse/IN-54205
       */
      const searchParams = new URLSearchParams({
        response_type: 'code',
        client_id: payload.clientId,
        redirect_uri: location.origin,
        state: `${payload.redirectUrl?.toString()}?provider=${ThirdpartyLoginMethod.Fb}`,
      }).toString();

      return `${ssoUrls[Page.FaceBook]}?${searchParams}`;
    }
    /**
     * path for testing: http://localhost:3000/zalo?clientId=3713985285292341361&provider=zalo&redirectUrl=http://localhost:5173
     */
    case Page.Zalo: {
      /**
       * 宣告內容用途說明與單號關聯
       * @description 建立 Zalo OAuth 所需查詢參數並序列化為 query string。
       * @purpose 因應 Zalo 參數命名與 state 格式，組合可用授權導向網址。
       * @external https://innotech.atlassian.net/browse/IN-54205
       */
      const searchParams = new URLSearchParams({
        app_id: payload.clientId,
        redirect_uri: location.origin, // Zalo does not support localhost
        state: `?state=${payload.redirectUrl?.toString()}?provider=${ThirdpartyLoginMethod.Zalo}`,
      }).toString();
      return `${ssoUrls[Page.Zalo]}?${searchParams}`;
    }
    case Page.Telegram: {
      /**
       * 宣告內容用途說明與單號關聯
       * @description 建立 Telegram Login Widget 所需查詢參數並序列化為 query string。
       * @purpose 將 bot 設定、存取權限與回跳網址整合為 Telegram 登入入口。
       * @external https://innotech.atlassian.net/browse/IN-54205
       */
      const searchParams = new URLSearchParams({
        bot_id: payload.clientId,
        origin: location.origin, // Must be the same domain as bot domain setting in telegram bot
        embed: '1',
        request_access: 'write',
        return_to: `${location.origin}?provider=${ThirdpartyLoginMethod.Telegram}&redirectUrl=${payload.redirectUrl}`,
      }).toString();
      return `${ssoUrls[Page.Telegram]}?${searchParams}`;
    }
    default:
      return '';
  }
};

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:24:54.077Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note Added required three-section annotation layout (top/middle/bottom), inserted declaration-level comments using only mapped ticket IN-54205 for declarations, normalized @external to full Jira browse URLs, retained existing runtime logic unchanged, and preserved non-required inline/testing comments.
 */
